import { z } from "zod";
import { idParamSchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { anyEntityTypeSchema, timestampsShape } from "./common.ts";
import { permissionSchema } from "./permissions.ts";

/**
 * AUTOMATIONS (PRD §8 Item 13) — the rule engine every later "when X then Y"
 * feature plugs into.
 *
 * A rule is `trigger → conditions → actions`, all three serialisable and all
 * three described by the catalogue endpoint, so the web editor is generic: it
 * renders whatever `GET /automations/catalogue` says exists rather than
 * hard-coding a list that drifts from the server.
 *
 * NOTHING here is evaluated as code. Conditions are a typed filter tree over a
 * flat fact bag built from the triggering record; actions are keys into a
 * registry. There is no expression language and no `eval` anywhere.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/** How a rule is set off. */
export const AUTOMATION_TRIGGER_KINDS = ["event", "schedule", "record_condition"] as const;
export type AutomationTriggerKind = (typeof AUTOMATION_TRIGGER_KINDS)[number];
export const automationTriggerKindSchema = z.enum(AUTOMATION_TRIGGER_KINDS);

/**
 * Sweep checks for `record_condition` triggers: a question asked of the whole
 * workspace once a day, for rules that react to the passage of time rather than
 * to a change ("invoice overdue by 7 days").
 */
export const AUTOMATION_RECORD_CHECKS = [
  "invoice_overdue_days",
  "task_overdue_days",
  "lead_idle_days",
  "lead_no_next_step_days",
  "client_idle_days",
  "client_no_next_step_days",
] as const;
export type AutomationRecordCheck = (typeof AUTOMATION_RECORD_CHECKS)[number];
export const automationRecordCheckSchema = z.enum(AUTOMATION_RECORD_CHECKS);

export const AUTOMATION_RECORD_CHECK_META: Record<AutomationRecordCheck, { label: string; entityType: string; description: string }> = {
  invoice_overdue_days: { label: "Invoice overdue by N days", entityType: "invoice", description: "An unpaid invoice whose due date passed N or more days ago." },
  task_overdue_days: { label: "Task overdue by N days", entityType: "task", description: "An open task whose due date passed N or more days ago." },
  lead_idle_days: { label: "Lead untouched for N days", entityType: "lead", description: "An open lead with no logged activity for N days." },
  lead_no_next_step_days: { label: "Lead with no next step", entityType: "lead", description: "An open lead with no open follow-up, idle for N days." },
  client_idle_days: { label: "Client untouched for N days", entityType: "client", description: "A live client with no logged activity for N days." },
  client_no_next_step_days: { label: "Client with no next step", entityType: "client", description: "A live client with no open follow-up, idle for N days." },
};

/** Lifecycle of one execution. `needs_attention` = waiting for a human (approval or a disabled rule). */
export const AUTOMATION_RUN_STATUSES = ["pending", "running", "succeeded", "failed", "skipped", "needs_attention"] as const;
export type AutomationRunStatus = (typeof AUTOMATION_RUN_STATUSES)[number];
export const automationRunStatusSchema = z.enum(AUTOMATION_RUN_STATUSES);

export const AUTOMATION_RUN_MODES = ["live", "dry_run"] as const;
export type AutomationRunMode = (typeof AUTOMATION_RUN_MODES)[number];
export const automationRunModeSchema = z.enum(AUTOMATION_RUN_MODES);

export const AUTOMATION_STEP_STATUSES = ["pending", "succeeded", "failed", "skipped"] as const;
export type AutomationStepStatus = (typeof AUTOMATION_STEP_STATUSES)[number];
export const automationStepStatusSchema = z.enum(AUTOMATION_STEP_STATUSES);

export const AUTOMATION_APPROVAL_STATES = ["not_required", "pending", "approved", "rejected"] as const;
export type AutomationApprovalState = (typeof AUTOMATION_APPROVAL_STATES)[number];
export const automationApprovalStateSchema = z.enum(AUTOMATION_APPROVAL_STATES);

/** Why a run did nothing. Surfaced verbatim in the run history so "it did not fire" is always answerable. */
export const AUTOMATION_SKIP_REASONS = [
  "conditions_not_met",
  "cooldown",
  "rate_limited_rule",
  "rate_limited_org",
  "loop_guard_self_trigger",
  "loop_guard_max_depth",
  "rule_disabled",
  "subject_missing",
  "approval_rejected",
  /** The workspace was read-only when the run came up: its licence had run out, or Lean2Automate had paused it. */
  "workspace_read_only",
] as const;
export type AutomationSkipReason = (typeof AUTOMATION_SKIP_REASONS)[number];
export const automationSkipReasonSchema = z.enum(AUTOMATION_SKIP_REASONS);

/**
 * Condition operators. `changed_to` / `changed_from` read the triggering
 * event's payload (`from`/`to`), everything else reads the fact bag.
 */
export const CONDITION_OPERATORS = [
  "eq",
  "neq",
  "in",
  "not_in",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "not_contains",
  "starts_with",
  "is_empty",
  "is_not_empty",
  "is_true",
  "is_false",
  "changed_to",
  "changed_from",
  "before",
  "after",
  "within_days",
  "older_than_days",
] as const;
export type ConditionOperator = (typeof CONDITION_OPERATORS)[number];
export const conditionOperatorSchema = z.enum(CONDITION_OPERATORS);

/** `arity: 0` operators take no value; the editor hides the value input for them. */
export const CONDITION_OPERATOR_META: Record<ConditionOperator, { label: string; arity: 0 | 1 | "many" }> = {
  eq: { label: "is", arity: 1 },
  neq: { label: "is not", arity: 1 },
  in: { label: "is any of", arity: "many" },
  not_in: { label: "is none of", arity: "many" },
  gt: { label: "is greater than", arity: 1 },
  gte: { label: "is at least", arity: 1 },
  lt: { label: "is less than", arity: 1 },
  lte: { label: "is at most", arity: 1 },
  contains: { label: "contains", arity: 1 },
  not_contains: { label: "does not contain", arity: 1 },
  starts_with: { label: "starts with", arity: 1 },
  is_empty: { label: "is empty", arity: 0 },
  is_not_empty: { label: "is not empty", arity: 0 },
  is_true: { label: "is yes", arity: 0 },
  is_false: { label: "is no", arity: 0 },
  changed_to: { label: "changed to", arity: 1 },
  changed_from: { label: "changed from", arity: 1 },
  before: { label: "is before", arity: 1 },
  after: { label: "is after", arity: 1 },
  within_days: { label: "is within (days)", arity: 1 },
  older_than_days: { label: "is older than (days)", arity: 1 },
};

/** Tokens resolved at evaluation time, so a rule can say "assigned to me" without freezing an id. */
export const CONDITION_TOKENS = ["$me", "$today", "$now"] as const;
export type ConditionToken = (typeof CONDITION_TOKENS)[number];

// ---------------------------------------------------------------------------
// The condition tree (INPUT only — never encoded, so recursion is safe here)
// ---------------------------------------------------------------------------

export type ConditionNode =
  | { all: ConditionNode[] }
  | { any: ConditionNode[] }
  | { not: ConditionNode }
  | { field: string; op: ConditionOperator; value?: unknown };

/** A serialisable AND/OR/NOT tree of `{ field, op, value }` leaves. Depth and width are capped. */
export const conditionNodeSchema: z.ZodType<ConditionNode> = z.lazy(() =>
  z.union([
    z.object({ all: z.array(conditionNodeSchema).min(1).max(20) }).strict(),
    z.object({ any: z.array(conditionNodeSchema).min(1).max(20) }).strict(),
    z.object({ not: conditionNodeSchema }).strict(),
    z
      .object({
        field: z.string().trim().min(1).max(120),
        op: conditionOperatorSchema,
        value: z.unknown().optional(),
      })
      .strict(),
  ]),
);

// ---------------------------------------------------------------------------
// Trigger + action inputs
// ---------------------------------------------------------------------------

/** What sets the rule off. One of three shapes, discriminated by `kind`. */
export const automationTriggerInputSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("event"),
      /** Outbox event types, e.g. ["lead.won"]. */
      eventTypes: z.array(z.string().trim().min(3).max(80)).min(1).max(20),
    })
    .strict(),
  z
    .object({
      kind: z.literal("schedule"),
      /** 5-field cron, evaluated in `timezone` (defaults to the workspace timezone). */
      cron: z.string().trim().min(1).max(120),
      timezone: z.string().trim().min(1).max(64).nullable().optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("record_condition"),
      check: automationRecordCheckSchema,
      days: z.number().int().min(0).max(365),
    })
    .strict(),
]);
export type AutomationTriggerInput = z.input<typeof automationTriggerInputSchema>;
export type AutomationTriggerData = z.output<typeof automationTriggerInputSchema>;

/** One step of a rule. `key` names a registered action; `params` is validated against that action's fields. */
export const automationActionInputSchema = z
  .object({
    key: z.string().trim().min(3).max(60),
    params: z.record(z.string(), z.unknown()),
    /** Keep going when this step fails (default false — a failed step stops the run). */
    continueOnError: z.boolean().optional(),
    /** Human label shown in the run log; defaults to the action definition's label. */
    label: z.string().trim().max(120).nullable().optional(),
  })
  .strict();
export type AutomationActionInput = z.input<typeof automationActionInputSchema>;
export type AutomationActionData = z.output<typeof automationActionInputSchema>;

// ---------------------------------------------------------------------------
// Response DTOs (encoded — every key present, no transforms, no defaults)
// ---------------------------------------------------------------------------

/** The trigger, flattened so the editor can read every key without narrowing first. */
export const automationTriggerSchema = z.object({
  kind: automationTriggerKindSchema,
  eventTypes: z.array(z.string()),
  cron: z.string().nullable(),
  timezone: z.string().nullable(),
  check: automationRecordCheckSchema.nullable(),
  days: z.number().int().nullable(),
});
export type AutomationTriggerDto = z.infer<typeof automationTriggerSchema>;

export const automationActionSchema = z.object({
  key: z.string(),
  label: z.string().nullable(),
  params: z.record(z.string(), z.unknown()),
  continueOnError: z.boolean(),
});
export type AutomationActionDto = z.infer<typeof automationActionSchema>;

/**
 * A rule. `conditions` is the stored tree — validated by `conditionNodeSchema`
 * on write and echoed here as plain JSON, because encoding a recursive schema
 * buys nothing the editor does not already have.
 */
export const automationRuleSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  enabled: z.boolean(),
  trigger: automationTriggerSchema,
  conditions: z.record(z.string(), z.unknown()).nullable(),
  actions: z.array(automationActionSchema),
  /** The member whose permissions the actions execute with (null = the workspace, all permissions). */
  runAsUserId: uuidSchema.nullable(),
  /**
   * True when that member has left and the rule fell back to the workspace.
   * US-13.6: it keeps running, and it is flagged here so somebody reassigns it.
   */
  runAsMissing: z.boolean(),
  /** Who owns the rule (US-13.6). Notified when it fails or is auto-disabled. */
  ownerUserId: uuidSchema.nullable(),
  version: z.number().int(),
  /** True when any action is client-facing: the run waits for a human (US-13.5). */
  requiresApproval: z.boolean(),
  /** Test mode — the rule evaluates and logs but performs nothing (US-13.3). */
  dryRun: z.boolean(),
  cooldownSeconds: z.number().int(),
  maxRunsPerHour: z.number().int(),
  templateKey: z.string().nullable(),
  tags: z.array(z.string()),
  lastRunAt: timestampSchema.nullable(),
  runCount: z.number().int(),
  successCount: z.number().int(),
  failureCount: z.number().int(),
  consecutiveFailures: z.number().int(),
  disabledAt: timestampSchema.nullable(),
  disabledReason: z.string().nullable(),
  nextRunAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type AutomationRuleDto = z.infer<typeof automationRuleSchema>;

/** One historical edit of a rule. A run records the version it executed, so history stays readable. */
export const automationRuleVersionSchema = z.object({
  id: uuidSchema,
  ruleId: uuidSchema,
  version: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  trigger: automationTriggerSchema,
  conditions: z.record(z.string(), z.unknown()).nullable(),
  actions: z.array(automationActionSchema),
  runAsUserId: uuidSchema.nullable(),
  createdByUserId: uuidSchema.nullable(),
  createdAt: timestampSchema,
});
export type AutomationRuleVersionDto = z.infer<typeof automationRuleVersionSchema>;

export const automationRunStepSchema = z.object({
  id: uuidSchema,
  index: z.number().int(),
  actionKey: z.string(),
  actionLabel: z.string().nullable(),
  status: automationStepStatusSchema,
  input: z.record(z.string(), z.unknown()),
  output: z.record(z.string(), z.unknown()).nullable(),
  error: z.string().nullable(),
  startedAt: timestampSchema.nullable(),
  finishedAt: timestampSchema.nullable(),
  durationMs: z.number().int().nullable(),
});
export type AutomationRunStepDto = z.infer<typeof automationRunStepSchema>;

export const automationRunSchema = z.object({
  id: uuidSchema,
  ruleId: uuidSchema,
  ruleName: z.string(),
  ruleVersion: z.number().int(),
  status: automationRunStatusSchema,
  mode: automationRunModeSchema,
  triggerKind: automationTriggerKindSchema,
  eventId: uuidSchema.nullable(),
  eventType: z.string().nullable(),
  subjectEntityType: anyEntityTypeSchema.nullable(),
  subjectEntityId: uuidSchema.nullable(),
  subjectLabel: z.string().nullable(),
  /** Causation depth: a run caused by another automation's change is 1 deeper (max 3, US-13.5). */
  depth: z.number().int(),
  parentRunId: uuidSchema.nullable(),
  approvalState: automationApprovalStateSchema,
  approvedByUserId: uuidSchema.nullable(),
  approvedAt: timestampSchema.nullable(),
  skipReason: automationSkipReasonSchema.nullable(),
  error: z.string().nullable(),
  startedAt: timestampSchema.nullable(),
  finishedAt: timestampSchema.nullable(),
  durationMs: z.number().int().nullable(),
  stepCount: z.number().int(),
  createdAt: timestampSchema,
});
export type AutomationRunDto = z.infer<typeof automationRunSchema>;

export const automationRunDetailSchema = automationRunSchema.extend({
  steps: z.array(automationRunStepSchema),
  /** Per-leaf record of what the evaluator compared, so "why did this not fire?" is answerable. */
  conditionTrace: z.array(
    z.object({
      field: z.string(),
      op: conditionOperatorSchema,
      expected: z.unknown(),
      actual: z.unknown(),
      passed: z.boolean(),
    }),
  ),
  subject: z.record(z.string(), z.unknown()),
});
export type AutomationRunDetailDto = z.infer<typeof automationRunDetailSchema>;

// ---------------------------------------------------------------------------
// The catalogue — what the generic rule editor renders from
// ---------------------------------------------------------------------------

export const AUTOMATION_PARAM_TYPES = ["string", "text", "number", "boolean", "uuid", "date", "select", "duration_minutes", "url", "template"] as const;
export type AutomationParamType = (typeof AUTOMATION_PARAM_TYPES)[number];

export const automationParamFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: z.enum(AUTOMATION_PARAM_TYPES),
  required: z.boolean(),
  help: z.string().nullable(),
  /** For `select` / `uuid`: the option list, or the entity type to pick from. */
  options: z.array(z.object({ value: z.string(), label: z.string() })).nullable(),
  entityType: anyEntityTypeSchema.nullable(),
  /**
   * For a `message_template` param: the channel its template must be on. The
   * editor offers only that channel's approved templates, and Save refuses
   * anything else (US-13.5).
   */
  templateChannel: z.enum(["email", "whatsapp"]).nullable(),
});
export type AutomationParamFieldDto = z.infer<typeof automationParamFieldSchema>;

export const automationActionDefinitionSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.string(),
  /** Client-facing actions force the rule into "requires approval" (US-13.5). */
  clientFacing: z.boolean(),
  permissions: z.array(permissionSchema),
  /** False when no module has registered an executor yet — the editor greys it out. */
  available: z.boolean(),
  params: z.array(automationParamFieldSchema),
});
export type AutomationActionDefinitionDto = z.infer<typeof automationActionDefinitionSchema>;

export const automationConditionFieldSchema = z.object({
  path: z.string(),
  label: z.string(),
  type: z.enum(["string", "number", "boolean", "date", "enum", "money", "uuid"]),
  group: z.string(),
  options: z.array(z.string()).nullable(),
  operators: z.array(conditionOperatorSchema),
});
export type AutomationConditionFieldDto = z.infer<typeof automationConditionFieldSchema>;

export const automationTriggerDefinitionSchema = z.object({
  key: z.string(),
  kind: automationTriggerKindSchema,
  label: z.string(),
  description: z.string(),
  /** For `event` triggers: the outbox types this entry subscribes to. */
  eventTypes: z.array(z.string()),
  subjectEntityType: anyEntityTypeSchema.nullable(),
  /** Condition field paths this trigger can offer (a subset of `conditionFields`). */
  fieldPaths: z.array(z.string()),
});
export type AutomationTriggerDefinitionDto = z.infer<typeof automationTriggerDefinitionSchema>;

export const automationTemplateSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string(),
  category: z.string(),
  trigger: automationTriggerSchema,
  conditions: z.record(z.string(), z.unknown()).nullable(),
  actions: z.array(automationActionSchema),
  requiresApproval: z.boolean(),
  /** False when one of the template's actions has no executor registered in this deployment. */
  available: z.boolean(),
});
export type AutomationTemplateDto = z.infer<typeof automationTemplateSchema>;

/** GET /api/v1/automations/catalogue (perm `automations:manage`) → everything the editor needs. */
export const automationCatalogueSchema = z.object({
  triggers: z.array(automationTriggerDefinitionSchema),
  actions: z.array(automationActionDefinitionSchema),
  conditionFields: z.array(automationConditionFieldSchema),
  operators: z.array(z.object({ key: conditionOperatorSchema, label: z.string(), arity: z.union([z.literal(0), z.literal(1), z.literal("many")]) })),
  recordChecks: z.array(z.object({ key: automationRecordCheckSchema, label: z.string(), entityType: z.string(), description: z.string() })),
  tokens: z.array(z.string()),
  limits: z.object({ maxActions: z.number().int(), maxDepth: z.number().int(), defaultCooldownSeconds: z.number().int(), orgRunsPerHour: z.number().int() }),
});
export type AutomationCatalogueDto = z.infer<typeof automationCatalogueSchema>;

// ---------------------------------------------------------------------------
// Request bodies
// ---------------------------------------------------------------------------

/** Writable shape WITHOUT defaults, so `.partial()` below cannot resurrect them (zod 4). */
const ruleWritableShape = {
  name: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).nullable(),
  enabled: z.boolean(),
  trigger: automationTriggerInputSchema,
  conditions: conditionNodeSchema.nullable(),
  actions: z.array(automationActionInputSchema).min(1).max(20),
  runAsUserId: uuidSchema.nullable(),
  ownerUserId: uuidSchema.nullable(),
  dryRun: z.boolean(),
  cooldownSeconds: z.number().int().min(0).max(86_400),
  maxRunsPerHour: z.number().int().min(1).max(1000),
  tags: tagsSchema,
};

/**
 * POST /api/v1/automations/rules (perm `automations:manage`) → 201 Rule.
 * An unknown action key or a bad param → 422 with `actions.<i>.params.<key>`.
 * A duplicate name → CONFLICT.
 */
export const createAutomationRuleInputSchema = z
  .object({
    ...ruleWritableShape,
    description: ruleWritableShape.description.optional(),
    enabled: ruleWritableShape.enabled.optional(),
    conditions: ruleWritableShape.conditions.optional(),
    runAsUserId: ruleWritableShape.runAsUserId.optional(),
    ownerUserId: ruleWritableShape.ownerUserId.optional(),
    dryRun: ruleWritableShape.dryRun.optional(),
    cooldownSeconds: ruleWritableShape.cooldownSeconds.optional(),
    maxRunsPerHour: ruleWritableShape.maxRunsPerHour.optional(),
    tags: ruleWritableShape.tags.optional(),
    /** Set when the rule was created from a recipe, for "reset to the recipe" and analytics. */
    templateKey: z.string().trim().max(80).optional(),
  })
  .strict();
export type CreateAutomationRuleInput = z.input<typeof createAutomationRuleInputSchema>;
export type CreateAutomationRuleData = z.output<typeof createAutomationRuleInputSchema>;

/** PATCH /api/v1/automations/rules/:id (perm `automations:manage`) → Rule. Merge patch; every edit makes a new version. */
export const updateAutomationRuleInputSchema = z.object(ruleWritableShape).partial().strict();
export type UpdateAutomationRuleInput = z.input<typeof updateAutomationRuleInputSchema>;
export type UpdateAutomationRuleData = z.output<typeof updateAutomationRuleInputSchema>;

/** POST /api/v1/automations/rules/:id/enable|disable (perm `automations:manage`) → Rule. */
export const setAutomationEnabledInputSchema = z.object({ reason: z.string().trim().max(500).nullable().optional() }).strict();
export type SetAutomationEnabledInput = z.input<typeof setAutomationEnabledInputSchema>;

/**
 * POST /api/v1/automations/rules/:id/test (perm `automations:manage`) → the dry run.
 * Give a real record to evaluate against, or nothing for the built-in sample.
 */
export const testAutomationRuleInputSchema = z
  .object({
    entityType: anyEntityTypeSchema.optional(),
    entityId: uuidSchema.optional(),
    /** Simulated event type, for rules whose conditions read `event.*`. */
    eventType: z.string().trim().max(80).optional(),
    /** Simulated `{ from, to }` change payload for `changed_to` / `changed_from`. */
    eventPayload: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();
export type TestAutomationRuleInput = z.input<typeof testAutomationRuleInputSchema>;

/** What a dry run reports: the facts it read, every condition comparison, and the steps it WOULD run. */
export const automationTestResultSchema = z.object({
  ruleId: uuidSchema,
  ruleVersion: z.number().int(),
  matched: z.boolean(),
  skipReason: automationSkipReasonSchema.nullable(),
  subjectEntityType: anyEntityTypeSchema.nullable(),
  subjectEntityId: uuidSchema.nullable(),
  subject: z.record(z.string(), z.unknown()),
  conditionTrace: z.array(z.object({ field: z.string(), op: conditionOperatorSchema, expected: z.unknown(), actual: z.unknown(), passed: z.boolean() })),
  plannedSteps: z.array(
    z.object({
      index: z.number().int(),
      actionKey: z.string(),
      actionLabel: z.string().nullable(),
      available: z.boolean(),
      clientFacing: z.boolean(),
      /** Null when `run_as` holds every permission the action needs. */
      missingPermissions: z.array(permissionSchema),
      resolvedParams: z.record(z.string(), z.unknown()),
      summary: z.string(),
    }),
  ),
  requiresApproval: z.boolean(),
  sample: z.boolean(),
});
export type AutomationTestResultDto = z.infer<typeof automationTestResultSchema>;

/** POST /api/v1/automations/runs/:id/approve|reject (perm `automations:manage`) → the run. */
export const decideAutomationRunInputSchema = z.object({ note: z.string().trim().max(500).nullable().optional() }).strict();
export type DecideAutomationRunInput = z.input<typeof decideAutomationRunInputSchema>;

/** POST /api/v1/automations/rules/from-template (perm `automations:manage`) → 201 Rule. */
export const createFromTemplateInputSchema = z
  .object({
    templateKey: z.string().trim().min(1).max(80),
    name: z.string().trim().min(1).max(160).optional(),
    enabled: z.boolean().optional(),
    runAsUserId: uuidSchema.nullable().optional(),
    /** Fills the template's placeholder params, e.g. `{ "notify.member": { "userIds": [...] } }`. */
    overrides: z.record(z.string(), z.record(z.string(), z.unknown())).optional(),
  })
  .strict();
export type CreateFromTemplateInput = z.input<typeof createFromTemplateInputSchema>;

export const listAutomationRulesQuerySchema = listQuerySchema.extend({
  enabled: z.enum(["true", "false"]).optional(),
  triggerKind: automationTriggerKindSchema.optional(),
  eventType: z.string().trim().max(80).optional(),
  ownerUserId: uuidSchema.optional(),
  sort: z.enum(["name", "lastRunAt", "runCount", "createdAt"]).optional(),
});
export type ListAutomationRulesQuery = z.infer<typeof listAutomationRulesQuerySchema>;

export const listAutomationRunsQuerySchema = listQuerySchema.extend({
  ruleId: uuidSchema.optional(),
  status: automationRunStatusSchema.optional(),
  mode: automationRunModeSchema.optional(),
  subjectEntityId: uuidSchema.optional(),
  sort: z.enum(["createdAt", "durationMs"]).optional(),
});
export type ListAutomationRunsQuery = z.infer<typeof listAutomationRunsQuerySchema>;

export const automationRunParamsSchema = idParamSchema;
export type AutomationRunParams = z.infer<typeof automationRunParamsSchema>;
